import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useBrandingStore } from '../store/branding'
import { useToast } from '../components/ui/Toast'
import { AddressAutocomplete } from '../components/AddressAutocomplete'
import type { StructuredAddress } from '../lib/mapbox'
import { Button } from '../components/ui/Button'

interface OnboardingProgress {
  current_step: number
  completed: number
  company_name: string | null
  company_phone: string | null
  company_address: string | null
  company_city: string | null
  company_state: string | null
  company_zip: string | null
  industry: string | null
  specializations: string | null
  company_size: string | null
  selected_features: string | null
  recommended_plan: string | null
  tools: string | null
  migrating_from: string | null
  first_client_name: string | null
  first_client_email: string | null
  first_client_phone: string | null
  invited_members: string | null
}

const INDUSTRIES = [
  { value: 'security_integrator', label: 'Security Integrator', icon: '\u{1F512}' },
  { value: 'av_integrator', label: 'AV Integrator', icon: '\u{1F3B5}' },
  { value: 'it_msp', label: 'IT / MSP', icon: '\u{1F4BB}' },
  { value: 'low_voltage', label: 'Low Voltage Contractor', icon: '\u26A1' },
  { value: 'fire_alarm', label: 'Fire & Life Safety', icon: '\u{1F525}' },
  { value: 'access_control', label: 'Access Control', icon: '\u{1F6AA}' },
  { value: 'networking', label: 'Network Infrastructure', icon: '\u{1F310}' },
  { value: 'other', label: 'Other', icon: '\u{1F3E2}' },
]

const COMPANY_SIZES = [
  { value: 'solo', label: 'Just me' },
  { value: '2-5', label: '2-5 people' },
  { value: '6-15', label: '6-15 people' },
  { value: '16-50', label: '16-50 people' },
  { value: '50+', label: '50+ people' },
]

const FEATURES = [
  { key: 'proposals', label: 'Proposals & Quoting', desc: 'Create and send professional proposals', plan: 'starter', icon: '\u{1F4CB}' },
  { key: 'dispatch', label: 'Work Orders', desc: 'Schedule and manage field work', plan: 'starter', icon: '\u{1F6E0}\uFE0F' },
  { key: 'tickets', label: 'Ticketing', desc: 'Track client issues with SLA', plan: 'professional', icon: '\u{1F3AB}' },
  { key: 'kb', label: 'Knowledge Base', desc: 'SOPs and client docs', plan: 'professional', icon: '\u{1F4DA}' },
  { key: 'ai_chat', label: 'AI Chat', desc: 'AI-powered client support', plan: 'professional', icon: '\u{1F916}' },
  { key: 'floor_plans', label: 'Floor Plans', desc: 'Device placement maps', plan: 'professional', icon: '\u{1F5FA}\uFE0F' },
  { key: 'time_tracking', label: 'Time Tracking', desc: 'Track hours by project', plan: 'starter', icon: '\u{23F1}\uFE0F' },
  { key: 'asset_tracking', label: 'Asset Tracking', desc: 'QR/barcode device tracking', plan: 'enterprise', icon: '\u{1F4F7}' },
]

const TOOLS = [
  { key: 'unifi', label: 'Ubiquiti UniFi', icon: '\u{1F4E1}' },
  { key: 'axis', label: 'Axis Communications', icon: '\u{1F4F9}' },
  { key: 'hikvision', label: 'Hikvision', icon: '\u{1F4F8}' },
  { key: 'dahua', label: 'Dahua', icon: '\u{1F4F7}' },
  { key: 'verkada', label: 'Verkada', icon: '\u{1F50D}' },
  { key: 'meraki', label: 'Cisco Meraki', icon: '\u{1F310}' },
  { key: 'dw_spectrum', label: 'DW Spectrum', icon: '\u{1F4FA}' },
  { key: 'milestone', label: 'Milestone XProtect', icon: '\u{1F3AC}' },
]

const MIGRATING_FROM_OPTIONS = [
  { key: 'portal_io', label: 'Portal.io' },
  { key: 'dtools_si', label: 'D-Tools SI' },
  { key: 'buildops', label: 'BuildOps' },
  { key: 'servicetitan', label: 'ServiceTitan' },
  { key: 'it_glue', label: 'IT Glue' },
  { key: 'system_surveyor', label: 'System Surveyor' },
  { key: 'companycam', label: 'CompanyCam' },
  { key: 'connectwise_datto', label: 'ConnectWise / Datto' },
  { key: 'quickbooks_standalone', label: 'QuickBooks (standalone)' },
  { key: 'spreadsheets', label: 'Spreadsheets / manual' },
  { key: 'other', label: 'Other' },
]

const STEPS = [
  { step: 1, title: 'Company Profile' },
  { step: 2, title: 'Your Industry' },
  { step: 3, title: 'Migrating From' },
  { step: 4, title: 'Pick Features' },
  { step: 5, title: 'Tools' },
  { step: 6, title: 'First Client' },
  { step: 7, title: 'Invite Team' },
  { step: 8, title: 'All Done!' },
]

export default function Onboarding() {
  const navigate = useNavigate()
  const { companyName, logoIcon } = useBrandingStore()
  const { toast, toastError } = useToast()

  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Step 1: Company Profile
  const [company, setCompany] = useState({ name: '', phone: '', address: '', city: '', state: '', zip: '' })
  // Step 2: Industry (multi-select)
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([])
  const [companySize, setCompanySize] = useState('')
  // Step 3: Migrating From (multi-select)
  const [migratingFrom, setMigratingFrom] = useState<string[]>([])
  const [migratingOther, setMigratingOther] = useState('')
  // Step 4: Features
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(['proposals', 'dispatch'])
  // Step 5: Tools
  const [selectedTools, setSelectedTools] = useState<string[]>([])
  // Step 6: Client
  const [client, setClient] = useState({ name: '', email: '', phone: '' })
  // Step 7: Team
  const [teamMembers, setTeamMembers] = useState<{ email: string; role: string }[]>([])
  const [newMember, setNewMember] = useState({ email: '', role: 'technician' })

  // Load existing progress
  useEffect(() => {
    const load = async () => {
      try {
        const progress = await api.get<OnboardingProgress>('/onboarding')
        if (progress.completed) {
          navigate('/')
          return
        }
        setStep(progress.current_step || 1)
        if (progress.company_name) setCompany(c => ({ ...c, name: progress.company_name || c.name, phone: progress.company_phone || c.phone, address: progress.company_address || c.address, city: progress.company_city || c.city, state: progress.company_state || c.state, zip: progress.company_zip || c.zip }))
        if (progress.industry) {
          try { setSelectedIndustries(JSON.parse(progress.industry)) } catch { setSelectedIndustries(progress.industry ? [progress.industry] : []) }
        }
        if (progress.company_size) setCompanySize(progress.company_size)
        if (progress.selected_features) {
          try { setSelectedFeatures(JSON.parse(progress.selected_features)) } catch { /* */ }
        }
        if (progress.tools) {
          try { setSelectedTools(JSON.parse(progress.tools)) } catch { /* */ }
        }
        if (progress.migrating_from) {
          try {
            const parsed = JSON.parse(progress.migrating_from)
            if (Array.isArray(parsed)) {
              setMigratingFrom(parsed.filter((v: string) => v !== '__other__'))
              const otherEntry = parsed.find((v: string) => v.startsWith('other:'))
              if (otherEntry) setMigratingOther(otherEntry.replace('other:', ''))
            }
          } catch { /* */ }
        }
        if (progress.first_client_name) setClient(c => ({ ...c, name: progress.first_client_name || c.name, email: progress.first_client_email || c.email, phone: progress.first_client_phone || c.phone }))
        if (progress.invited_members) {
          try { setTeamMembers(JSON.parse(progress.invited_members)) } catch { /* */ }
        }
      } catch { /* first time — start fresh */ }
      finally { setLoading(false) }
    }
    load()
  }, [navigate])

  const saveStep = useCallback(async (nextStep: number) => {
    setSaving(true)
    try {
      const data: Record<string, unknown> = {}
      if (step === 1) {
        Object.assign(data, { company_name: company.name, company_phone: company.phone, company_address: company.address, company_city: company.city, company_state: company.state, company_zip: company.zip })
      } else if (step === 2) {
        Object.assign(data, { industry: JSON.stringify(selectedIndustries), company_size: companySize })
      } else if (step === 3) {
        const migData = [...migratingFrom]
        if (migratingFrom.includes('other') && migratingOther.trim()) {
          migData.push(`other:${migratingOther.trim()}`)
        }
        Object.assign(data, { migrating_from: JSON.stringify(migData) })
      } else if (step === 4) {
        const plan = getRecommendedPlan(selectedFeatures)
        Object.assign(data, { selected_features: JSON.stringify(selectedFeatures), recommended_plan: plan })
      } else if (step === 5) {
        Object.assign(data, { tools: JSON.stringify(selectedTools) })
      } else if (step === 6) {
        Object.assign(data, { first_client_name: client.name, first_client_email: client.email, first_client_phone: client.phone })
      } else if (step === 7) {
        Object.assign(data, { invited_members: JSON.stringify(teamMembers) })
      }

      await api.patch('/onboarding', { step: nextStep, data })
      setStep(nextStep)
    } catch (err) {
      toastError('Failed to save progress', err)
    } finally {
      setSaving(false)
    }
  }, [step, company, selectedIndustries, companySize, migratingFrom, migratingOther, selectedFeatures, selectedTools, client, teamMembers, toastError])

  const handleComplete = async () => {
    setSaving(true)
    try {
      // Create first client if provided
      if (client.name) {
        await api.post('/onboarding/client', { name: client.name, email: client.email || undefined, phone: client.phone || undefined })
      }
      await api.post('/onboarding/complete', { features: selectedFeatures })
      toast('Welcome aboard! Your portal is ready.')
      navigate('/')
    } catch (err) {
      toastError('Failed to complete setup', err)
    } finally {
      setSaving(false)
    }
  }

  const handleSkip = async () => {
    try {
      await api.post('/onboarding/skip')
      navigate('/')
    } catch (err) {
      toastError('Failed to skip', err)
    }
  }

  function getRecommendedPlan(features: string[]): string {
    const enterprise = ['asset_tracking', 'provisioning']
    const pro = ['tickets', 'kb', 'ai_chat', 'floor_plans']
    if (features.some(f => enterprise.includes(f))) return 'Enterprise'
    if (features.some(f => pro.includes(f))) return 'Professional'
    return 'Starter'
  }

  const toggleIndustry = (key: string) => {
    setSelectedIndustries(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])
  }

  const toggleFeature = (key: string) => {
    setSelectedFeatures(prev => prev.includes(key) ? prev.filter(f => f !== key) : [...prev, key])
  }

  const toggleTool = (key: string) => {
    setSelectedTools(prev => prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key])
  }

  const toggleMigratingFrom = (key: string) => {
    setMigratingFrom(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])
  }

  const addTeamMember = () => {
    if (!newMember.email || !newMember.email.includes('@')) return
    setTeamMembers(prev => [...prev, { ...newMember }])
    setNewMember({ email: '', role: 'technician' })
  }

  const removeTeamMember = (idx: number) => {
    setTeamMembers(prev => prev.filter((_, i) => i !== idx))
  }

  const handleAddressChange = (addr: StructuredAddress) => {
    setCompany(c => ({
      ...c,
      address: addr.address_line1 || c.address,
      city: addr.city || c.city,
      state: addr.state || c.state,
      zip: addr.zip_code || c.zip,
    }))
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-app)' }}>
        <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Loading...</div>
      </div>
    )
  }

  const progress = ((step - 1) / (STEPS.length - 1)) * 100

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-app)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '20px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src={logoIcon || '/Amber Global icon.png'} alt={companyName} style={{ width: 32, height: 32, borderRadius: 8 }} />
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{companyName || 'Portal Setup'}</span>
        </div>
        <button onClick={() => { if (confirm('Skip the rest of setup? You can always finish later in Settings.')) handleSkip() }} className="btn btn-ghost">
          Finish later
        </button>
      </div>

      {/* Progress bar */}
      <div style={{ padding: '0 32px', marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Step {step} of {STEPS.length}</span>
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{STEPS[step - 1]?.title}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {STEPS.map((s, i) => {
            const stepIndex = i + 1
            const isCompleted = stepIndex < step
            const isCurrent = stepIndex === step
            return (
              <div
                key={s.step}
                onClick={() => isCompleted ? setStep(stepIndex) : undefined}
                title={isCompleted ? `Go back to: ${s.title}` : s.title}
                style={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  background: isCompleted
                    ? 'linear-gradient(90deg, var(--accent-h), var(--accent))'
                    : isCurrent
                      ? 'var(--accent-h)'
                      : 'var(--border)',
                  cursor: isCompleted ? 'pointer' : 'default',
                  transition: 'background 300ms ease',
                  opacity: isCurrent ? 0.6 : 1,
                }}
              />
            )
          })}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '40px 32px' }}>
        <div style={{ width: '100%', maxWidth: 600 }}>

          {/* Step 1: Company Profile */}
          {step === 1 && (
            <div className="page-enter">
              <h2 style={headingStyle}>Tell us about your company</h2>
              <p style={subtitleStyle}>We'll use this to customize your portal.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 24 }}>
                <div>
                  <label style={labelStyle}>Company Name *</label>
                  <input className="v3-input" value={company.name} onChange={e => setCompany({ ...company, name: e.target.value })} placeholder="Acme Security" />
                </div>
                <div>
                  <label style={labelStyle}>Phone</label>
                  <input className="v3-input" value={company.phone} onChange={e => setCompany({ ...company, phone: e.target.value })} placeholder="(555) 123-4567" />
                </div>
                <div>
                  <label style={labelStyle}>Address</label>
                  <AddressAutocomplete
                    value={{
                      address_line1: company.address,
                      city: company.city,
                      state: company.state,
                      zip_code: company.zip,
                    }}
                    onChange={handleAddressChange}
                    placeholder="Start typing an address..."
                    showMapPreview={false}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px', gap: 12 }}>
                  <div>
                    <label style={labelStyle}>City</label>
                    <input className="v3-input" value={company.city} onChange={e => setCompany({ ...company, city: e.target.value })} />
                  </div>
                  <div>
                    <label style={labelStyle}>State</label>
                    <input className="v3-input" value={company.state} onChange={e => setCompany({ ...company, state: e.target.value })} />
                  </div>
                  <div>
                    <label style={labelStyle}>ZIP</label>
                    <input className="v3-input" value={company.zip} onChange={e => setCompany({ ...company, zip: e.target.value })} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Industry (multi-select) */}
          {step === 2 && (
            <div className="page-enter">
              <h2 style={headingStyle}>What industries are you in?</h2>
              <p style={subtitleStyle}>Select all that apply — many integrators do multiple trades.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginTop: 24 }}>
                {INDUSTRIES.map(ind => {
                  const selected = selectedIndustries.includes(ind.value)
                  return (
                    <button
                      key={ind.value}
                      onClick={() => toggleIndustry(ind.value)}
                      style={{
                        padding: '16px', borderRadius: 12, border: selected ? '2px solid var(--accent-h)' : '1px solid var(--border)',
                        background: selected ? 'rgba(251, 191, 36, 0.08)' : 'var(--bg-surface)',
                        cursor: 'pointer', textAlign: 'center', transition: 'all 150ms',
                        position: 'relative',
                      }}
                    >
                      <div style={{ fontSize: 28, marginBottom: 8 }}>{ind.icon}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{ind.label}</div>
                      {selected && (
                        <div style={{
                          position: 'absolute', top: 6, right: 6, width: 18, height: 18, borderRadius: 4,
                          background: 'var(--accent-h)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent-t)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
              <div style={{ marginTop: 24 }}>
                <label style={labelStyle}>Company Size</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {COMPANY_SIZES.map(s => (
                    <button
                      key={s.value}
                      onClick={() => setCompanySize(s.value)}
                      style={{
                        padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer',
                        border: companySize === s.value ? '2px solid var(--accent-h)' : '1px solid var(--border)',
                        background: companySize === s.value ? 'rgba(251, 191, 36, 0.08)' : 'transparent',
                        color: 'var(--text)',
                      }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Migrating From */}
          {step === 3 && (
            <div className="page-enter">
              <h2 style={headingStyle}>What tools are you currently using?</h2>
              <p style={subtitleStyle}>Select all that apply. This helps us tailor your migration.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 24 }}>
                {MIGRATING_FROM_OPTIONS.map(opt => {
                  const selected = migratingFrom.includes(opt.key)
                  return (
                    <button
                      key={opt.key}
                      onClick={() => toggleMigratingFrom(opt.key)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                        borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                        border: selected ? '2px solid var(--accent-h)' : '1px solid var(--border)',
                        background: selected ? 'rgba(251, 191, 36, 0.06)' : 'var(--bg-surface)',
                        transition: 'all 150ms',
                      }}
                    >
                      <div style={{
                        width: 22, height: 22, borderRadius: 6, border: selected ? '2px solid var(--accent-h)' : '1px solid var(--border)',
                        background: selected ? 'var(--accent-h)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        {selected && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent-t)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                      </div>
                      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{opt.label}</span>
                    </button>
                  )
                })}
              </div>
              {migratingFrom.includes('other') && (
                <div style={{ marginTop: 16 }}>
                  <label style={labelStyle}>What other tools?</label>
                  <input
                    className="v3-input"
                    value={migratingOther}
                    onChange={e => setMigratingOther(e.target.value)}
                    placeholder="Enter the tools you're using..."
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 4: Features */}
          {step === 4 && (
            <div className="page-enter">
              <h2 style={headingStyle}>What do you need?</h2>
              <p style={subtitleStyle}>Select the features you want. We'll recommend the right plan.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 24 }}>
                {FEATURES.map(f => {
                  const selected = selectedFeatures.includes(f.key)
                  return (
                    <button
                      key={f.key}
                      onClick={() => toggleFeature(f.key)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 16, padding: '14px 16px',
                        borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                        border: selected ? '2px solid var(--accent-h)' : '1px solid var(--border)',
                        background: selected ? 'rgba(251, 191, 36, 0.06)' : 'var(--bg-surface)',
                        transition: 'all 150ms',
                      }}
                    >
                      <span style={{ fontSize: 22, flexShrink: 0 }}>{f.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{f.label}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{f.desc}</div>
                      </div>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                        textTransform: 'uppercase',
                        background: f.plan === 'enterprise' ? 'rgba(139, 92, 246, 0.15)' : f.plan === 'professional' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(34, 197, 94, 0.15)',
                        color: f.plan === 'enterprise' ? 'var(--color-cable)' : f.plan === 'professional' ? 'var(--info)' : 'var(--ok)',
                      }}>
                        {f.plan}
                      </span>
                      <div style={{
                        width: 22, height: 22, borderRadius: 6, border: selected ? '2px solid var(--accent-h)' : '1px solid var(--border)',
                        background: selected ? 'var(--accent-h)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        {selected && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent-t)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                      </div>
                    </button>
                  )
                })}
              </div>
              {selectedFeatures.length > 0 && (
                <div style={{ marginTop: 20, padding: '12px 16px', borderRadius: 10, background: 'rgba(251, 191, 36, 0.06)', border: '1px solid rgba(251, 191, 36, 0.2)' }}>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Recommended plan</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent-h)', marginTop: 4 }}>
                    {getRecommendedPlan(selectedFeatures)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Tools */}
          {step === 5 && (
            <div className="page-enter">
              <h2 style={headingStyle}>What tools do you use?</h2>
              <p style={subtitleStyle}>Select the brands and platforms you work with.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginTop: 24 }}>
                {TOOLS.map(t => {
                  const selected = selectedTools.includes(t.key)
                  return (
                    <button
                      key={t.key}
                      onClick={() => toggleTool(t.key)}
                      style={{
                        padding: '14px', borderRadius: 10, cursor: 'pointer', textAlign: 'center',
                        border: selected ? '2px solid var(--accent-h)' : '1px solid var(--border)',
                        background: selected ? 'rgba(251, 191, 36, 0.06)' : 'var(--bg-surface)',
                        transition: 'all 150ms',
                      }}
                    >
                      <span style={{ fontSize: 24, display: 'block', marginBottom: 6 }}>{t.icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{t.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Step 6: First Client */}
          {step === 6 && (
            <div className="page-enter">
              <h2 style={headingStyle}>Add your first client</h2>
              <p style={subtitleStyle}>You can skip this and add clients later.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 24 }}>
                <div>
                  <label style={labelStyle}>Client / Company Name</label>
                  <input className="v3-input" value={client.name} onChange={e => setClient({ ...client, name: e.target.value })} placeholder="Acme Corp" />
                </div>
                <div>
                  <label style={labelStyle}>Contact Email</label>
                  <input className="v3-input" type="email" value={client.email} onChange={e => setClient({ ...client, email: e.target.value })} placeholder="contact@acme.com" />
                </div>
                <div>
                  <label style={labelStyle}>Phone</label>
                  <input className="v3-input" value={client.phone} onChange={e => setClient({ ...client, phone: e.target.value })} placeholder="(555) 999-0000" />
                </div>
              </div>
            </div>
          )}

          {/* Step 7: Invite Team */}
          {step === 7 && (
            <div className="page-enter">
              <h2 style={headingStyle}>Invite your team</h2>
              <p style={subtitleStyle}>Add team members to your portal. You can do this later too.</p>
              <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
                <input className="v3-input" style={{ flex: 1 }} type="email" placeholder="team@example.com" value={newMember.email} onChange={e => setNewMember({ ...newMember, email: e.target.value })} />
                <div className="v3-select-wrap" style={{ width: 140 }}>
                  <select className="v3-select" value={newMember.role} onChange={e => setNewMember({ ...newMember, role: e.target.value })}>
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                    <option value="technician">Technician</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <span className="v3-select-chevron">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6,9 12,15 18,9" />
                    </svg>
                  </span>
                </div>
                <Button onClick={addTeamMember} style={{ flexShrink: 0 }}>Add</Button>
              </div>
              {teamMembers.length > 0 && (
                <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {teamMembers.map((m, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', borderRadius: 8, background: 'var(--bg-surface)', border: '1px solid var(--border)',
                    }}>
                      <div>
                        <span style={{ fontSize: 14, color: 'var(--text)' }}>{m.email}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginLeft: 8, textTransform: 'capitalize' }}>{m.role}</span>
                      </div>
                      <button onClick={() => removeTeamMember(i)} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: 16 }}>&times;</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 8: Done */}
          {step === 8 && (
            <div className="page-enter" style={{ textAlign: 'center', paddingTop: 40 }}>
              <div style={{ fontSize: 64, marginBottom: 16 }}>{'\u{1F389}'}</div>
              <h2 style={{ ...headingStyle, marginBottom: 12 }}>You're all set!</h2>
              <p style={{ ...subtitleStyle, maxWidth: 400, margin: '0 auto 32px' }}>
                Your portal is configured and ready to go. Start creating proposals, managing clients, and growing your business.
              </p>
              {selectedFeatures.length > 0 && (
                <div style={{ marginBottom: 32, display: 'inline-flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                  {selectedFeatures.map(f => {
                    const feat = FEATURES.find(x => x.key === f)
                    return feat ? (
                      <span key={f} style={{
                        padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                        background: 'rgba(251, 191, 36, 0.1)', color: 'var(--accent-h)', border: '1px solid rgba(251, 191, 36, 0.2)',
                      }}>
                        {feat.icon} {feat.label}
                      </span>
                    ) : null
                  })}
                </div>
              )}
            </div>
          )}

          {/* Navigation buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 40, paddingTop: 24, borderTop: '1px solid var(--border)' }}>
            {step > 1 ? (
              <button onClick={() => setStep(step - 1)} style={{
                padding: '10px 20px', borderRadius: 8, background: 'transparent', border: '1px solid var(--border)',
                color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              }}>
                Back
              </button>
            ) : <div />}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {step >= 2 && step <= 7 && (
                <button onClick={() => saveStep(step + 1)} style={{
                  padding: '10px 16px', borderRadius: 8, background: 'transparent', border: 'none',
                  color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: 13, fontWeight: 500,
                }}>
                  Skip for now
                </button>
              )}

              {step < 8 ? (
                <Button onClick={() => saveStep(step + 1)} disabled={step === 1 && !company.name} loading={saving}>
                  Continue
                </Button>
              ) : (
                <Button onClick={handleComplete} loading={saving}>
                  Launch Portal
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const headingStyle: React.CSSProperties = {
  fontSize: 24, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.02em', margin: 0,
}
const subtitleStyle: React.CSSProperties = {
  fontSize: 14, color: 'var(--text-secondary)', marginTop: 8,
}
const labelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6,
}
